import React from "react";
import {
    Box,
    Typography,
    Grid,
    Container,
    useTheme,
    alpha,
    Paper,
    Button,
} from "@mui/material";

import { motion } from "framer-motion";
import Footer from "./Footer";

import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import CallOutlinedIcon from "@mui/icons-material/CallOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";

const contactDetails = [
    {
        title: "Our Office",
        Icon: LocationOnOutlinedIcon,
        value: (
            <>
                Plot No C-9, Asha Officers Colony,
                <br />
                RK Puram, Secunderabad, Hyderabad
            </>
        ),
        href: "https://www.google.com/maps/search/?api=1&query=Plot+No+C-9+Asha+Officers+Colony+RK+Puram+Secunderabad+Hyderabad",
        accent: "#007BFF",
    },
    {
        title: "Call Us",
        Icon: CallOutlinedIcon,
        value: "+91 95950 27710",
        href: "tel:+919595027710",
        accent: "#08A88A",
    },
    {
        title: "Email Us",
        Icon: EmailOutlinedIcon,
        value: "support@arthgyan.com",
        href: "mailto:support@arthgyan.com",
        accent: "#E6A126",
    },
];

const ContactUs = () => {
    const theme = useTheme();

    const MotionBox = motion(Box);
    const MotionPaper = motion(Paper);

    return (
        <>
            <Box
                sx={{
                    pt: { xs: 10, md: 14 },
                    pb: { xs: 6, md: 2 },
                    px: 2,

                }}
            >
                <Container maxWidth="md">
                    <Typography
                        sx={{
                            fontSize: { xs: "20px", sm: "28px", md: "40px" },
                            fontWeight: 600,
                            textAlign: "center",
                            lineHeight: 1.1,
                            mb: 3,
                            animation: "fadeUp 0.9s ease-out both",
                            color: theme.palette.text.primary,
                            position: "relative",
                            display: "inline-block",
                            width: "100%",
                        }}
                    >
                        Get in{" "}
                        <Box
                            component="span"
                            sx={{
                                fontWeight: 900,
                                background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.text.primary})`,
                                WebkitBackgroundClip: "text",
                                WebkitTextFillColor: "transparent",
                                position: "relative",
                                display: "inline-block",
                                "&::after": {
                                    content: '""',
                                    position: "absolute",
                                    bottom: "-4px",
                                    left: 0,
                                    width: "100%",
                                    height: "2px",
                                    background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, transparent 100%)`,
                                }
                            }}
                        >
                            Touch
                        </Box>{" "}
                        with us
                    </Typography>

                    <Typography
                        variant="body1"

                        sx={{
                            textAlign: "center",
                            fontSize: { xs: "12px", sm: "14px", md: "16px" },
                            color: "#555",
                            maxWidth: 640,
                            mx: "auto",
                        }}
                    >
                        We’re here to help you. Whether you have questions, need
                        support, or want to collaborate — just reach out. Our
                        team is ready to assist you on your financial journey.
                    </Typography>
                </Container>
            </Box>


            {/* CONTACT CONTENT */}
            < Container
                maxWidth="lg"
                sx={{
                    pt: { xs: 1, md: 2 },
                    pb: { xs: 7, md: 10 },
                }}
            >
                <Grid
                    container
                    spacing={{ xs: 4, md: 6 }}
                    alignItems="center"
                >
                    {/* LEFT: INDIA MAP */}
                    <Grid item xs={12} md={6}>
                        <MotionBox
                            initial={{ opacity: 0, x: -30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true, amount: 0.2 }}
                            transition={{ duration: 0.65 }}
                        >
                            <Box
                                sx={{
                                    position: "relative",
                                    p: { xs: 2, sm: 3, md: 4 },
                                    borderRadius: { xs: 4, md: 5 },
                                    overflow: "hidden",
                                    background: `linear-gradient(145deg,
                    ${alpha(theme.palette.primary.main, 0.075)},
                    ${alpha(theme.palette.primary.main, 0.025)})`,
                                    border: `1px solid ${alpha(
                                        theme.palette.primary.main,
                                        0.11
                                    )}`,
                                }}
                            >
                                <Box
                                    sx={{
                                        position: "absolute",
                                        top: 20,
                                        right: 20,
                                        width: 65,
                                        height: 65,
                                        borderRadius: "50%",
                                        border: `1px dashed ${alpha(
                                            theme.palette.primary.main,
                                            0.25
                                        )}`,
                                    }}
                                />

                                <Typography
                                    sx={{
                                        position: "relative",
                                        zIndex: 1,
                                        fontSize: 12,
                                        letterSpacing: 1.5,
                                        fontWeight: 800,
                                        color: theme.palette.primary.main,
                                        mb: 1,
                                    }}
                                >
                                    WE'RE HERE FOR YOU
                                </Typography>

                                <Typography
                                    component="h2"
                                    sx={{
                                        position: "relative",
                                        zIndex: 1,
                                        fontSize: { xs: 23, sm: 27, md: 31 },
                                        fontWeight: 750,
                                        lineHeight: 1.35,
                                        color: theme.palette.text.primary,
                                        maxWidth: 390,
                                    }}
                                >
                                    Your Financial Journey, Our Priority.
                                </Typography>

                                <Typography
                                    sx={{
                                        position: "relative",
                                        zIndex: 1,
                                        mt: 1,
                                        maxWidth: 390,
                                        fontSize: 14,
                                        lineHeight: 1.8,
                                        color: theme.palette.text.secondary,
                                    }}
                                >
                                    From your first question to ongoing assistance, we’re just
                                    a message away.
                                </Typography>

                                <Box
                                    component="img"
                                    src="https://img.freepik.com/premium-vector/india-dotted-map-modern-halftone-style_141130-19154.jpg"
                                    alt="India map representing ArthGyan's presence in India"
                                    loading="lazy"
                                    sx={{
                                        display: "block",
                                        width: "100%",
                                        height: { xs: 240, sm: 300, md: 330 },
                                        objectFit: "contain",
                                        mt: { xs: 1, md: 2 },
                                        filter: "saturate(0.85)",
                                        mixBlendMode: "multiply",
                                    }}
                                />

                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                        mt: 1,
                                        px: 1.5,
                                        py: 1.3,
                                        borderRadius: 2.5,
                                        backgroundColor: alpha(
                                            theme.palette.background.paper,
                                            0.85
                                        ),
                                        border: `1px solid ${alpha(
                                            theme.palette.primary.main,
                                            0.1
                                        )}`,
                                    }}
                                >
                                    <LocationOnOutlinedIcon
                                        sx={{
                                            color: theme.palette.primary.main,
                                            fontSize: 21,
                                        }}
                                    />

                                    <Typography
                                        sx={{
                                            fontSize: 13,
                                            fontWeight: 600,
                                            color: theme.palette.text.primary,
                                        }}
                                    >
                                        Based in Hyderabad, India
                                    </Typography>
                                </Box>
                            </Box>
                        </MotionBox>
                    </Grid>

                    {/* RIGHT: CONTACT DETAILS */}
                    <Grid item xs={12} md={6}>
                        <MotionBox
                            initial={{ opacity: 0, x: 30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true, amount: 0.2 }}
                            transition={{ duration: 0.65 }}
                        >
                            <Typography
                                sx={{
                                    fontSize: { xs: 23, md: 29 },
                                    fontWeight: 800,
                                    color: theme.palette.text.primary,
                                    mb: 1,
                                }}
                            >
                                Let's Connect
                            </Typography>

                            <Typography
                                sx={{
                                    fontSize: 14,
                                    lineHeight: 1.8,
                                    color: theme.palette.text.secondary,
                                    mb: 3,
                                }}
                            >
                                Reach out using any of the options below. We’d love to hear
                                from you.
                            </Typography>

                            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                {contactDetails.map((item, index) => {
                                    const ContactIcon = item.Icon;

                                    return (
                                        <MotionPaper
                                            key={item.title}
                                            component="a"
                                            href={item.href}
                                            target={index === 0 ? "_blank" : undefined}
                                            rel={index === 0 ? "noopener noreferrer" : undefined}
                                            elevation={0}
                                            whileHover={{ x: 5 }}
                                            transition={{ duration: 0.2 }}
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: { xs: 1.8, sm: 2.2 },
                                                p: { xs: 2, sm: 2.5 },
                                                borderRadius: 3,
                                                textDecoration: "none",
                                                color: "inherit",
                                                backgroundColor: theme.palette.background.paper,
                                                border: `1px solid ${alpha(
                                                    theme.palette.primary.main,
                                                    0.12
                                                )}`,
                                                boxShadow: `0 5px 20px ${alpha(
                                                    theme.palette.primary.dark,
                                                    0.035
                                                )}`,
                                                transition: "border-color 0.25s, box-shadow 0.25s",
                                                "&:hover": {
                                                    borderColor: alpha(item.accent, 0.5),
                                                    boxShadow: `0 10px 26px ${alpha(
                                                        item.accent,
                                                        0.1
                                                    )}`,
                                                },
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    flexShrink: 0,
                                                    width: { xs: 46, sm: 52 },
                                                    height: { xs: 46, sm: 52 },
                                                    borderRadius: 2.5,
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    color: item.accent,
                                                    backgroundColor: alpha(item.accent, 0.1),
                                                }}
                                            >
                                                <ContactIcon sx={{ fontSize: 26 }} />
                                            </Box>

                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography
                                                    sx={{
                                                        fontSize: 12,
                                                        fontWeight: 700,
                                                        letterSpacing: 0.4,
                                                        color: theme.palette.text.secondary,
                                                        mb: 0.5,
                                                    }}
                                                >
                                                    {item.title}
                                                </Typography>

                                                <Typography
                                                    sx={{
                                                        fontSize: { xs: 13, sm: 14 },
                                                        lineHeight: 1.8,
                                                        fontWeight: 600,
                                                        overflowWrap: "anywhere",
                                                        color: theme.palette.text.primary,
                                                    }}
                                                >
                                                    {item.value}
                                                </Typography>
                                            </Box>

                                            <ArrowForwardRoundedIcon
                                                sx={{
                                                    flexShrink: 0,
                                                    fontSize: 21,
                                                    color: item.accent,
                                                }}
                                            />
                                        </MotionPaper>
                                    );
                                })}
                            </Box>

                            {/* CTA */}
                            <Box
                                sx={{
                                    mt: 3,
                                    p: { xs: 2.5, sm: 3 },
                                    borderRadius: 3,
                                    background: `linear-gradient(120deg,
                    ${theme.palette.primary.main},
                    ${theme.palette.primary.dark})`,
                                    color: "#ffffff",
                                    display: "flex",
                                    alignItems: { xs: "flex-start", sm: "center" },
                                    justifyContent: "space-between",
                                    gap: 2,
                                    flexDirection: { xs: "column", sm: "row" },
                                }}
                            >
                                <Box>
                                    <Typography
                                        sx={{ fontSize: 17, fontWeight: 750, mb: 0.5 }}
                                    >
                                        Need assistance?
                                    </Typography>

                                    <Typography
                                        sx={{
                                            fontSize: 13,
                                            lineHeight: 1.7,
                                            color: alpha("#ffffff", 0.88),
                                        }}
                                    >
                                        Our team is ready to help you.
                                    </Typography>
                                </Box>

                                <Button
                                    component="a"
                                    href="mailto:support@arthgyan.com"
                                    variant="contained"
                                    endIcon={<ArrowForwardRoundedIcon />}
                                    sx={{
                                        flexShrink: 0,
                                        px: 2.3,
                                        py: 1.1,
                                        borderRadius: 2,
                                        textTransform: "none",
                                        fontSize: 13,
                                        fontWeight: 700,
                                        color: theme.palette.primary.dark,
                                        backgroundColor: "#ffffff",
                                        boxShadow: "none",
                                        "&:hover": {
                                            backgroundColor: "#F0F6FF",
                                            boxShadow: "none",
                                        },
                                    }}
                                >
                                    Contact Support
                                </Button>
                            </Box>
                        </MotionBox>
                    </Grid>
                </Grid>
            </Container >

            <Footer />
        </>
    );
};

export default ContactUs;
